import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import './PostDetail.css';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext);
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/posts/${postId}`);
      setPost(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPost();
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !isAuthenticated) return;

    try {
      setSubmittingComment(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/posts/${postId}/comment`, 
        { content: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommentText('');
      fetchPost();
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/posts/${postId}/comment/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPost();
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  const changeImage = (direction) => {
    if (!post?.images || post.images.length <= 1) return;
    
    setCurrentImageIndex(prev => {
      const maxIndex = post.images.length - 1;
      if (direction === 'next') {
        return prev >= maxIndex ? 0 : prev + 1;
      } else {
        return prev <= 0 ? maxIndex : prev - 1;
      }
    });
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="post-detail-container">
        <div className="post-detail-loading">
          <div className="spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="post-detail-container">
        <div className="post-detail-error">
          <h2>😕 {error || 'Post not found'}</h2>
          <button onClick={() => navigate(-1)} className="back-button">
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const isLiked = isAuthenticated && post.likes?.includes(user._id);
  const isOwner = isAuthenticated && post.userId?._id === user._id;

  return (
    <div className="post-detail-container">
      <div className="post-detail-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
      </div>

      <div className="post-detail-content">
        <div className="post-detail-card">
          {/* Author Info */}
          <div className="post-author">
            <Link to={`/profile/${post.userId?.username}`} className="author-avatar">
              {post.userId?.profile?.avatar?.url ? (
                <img src={post.userId.profile.avatar.url} alt={post.userId.username} />
              ) : (
                <div className="avatar-placeholder">
                  {post.userId?.username?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </Link>
            <div className="author-info">
              <Link to={`/profile/${post.userId?.username}`} className="author-name">
                {post.userId?.username}
              </Link>
              {post.userId?.accountType && (
                <span className={`badge-${post.userId.accountType}`}>
                  {post.userId.accountType === 'developer' ? '🎮 Dev' : '✨'}
                </span>
              )}
              <span className="post-timestamp">{formatTimeAgo(post.createdAt)}</span>
            </div>
          </div>

          {/* Post Content */}
          <div className="post-content">
            <p>{post.content}</p>
          </div>

          {/* Post Images */}
          {post.images && post.images.length > 0 && (
            <div className="post-images">
              <img 
                src={post.images[currentImageIndex]?.url} 
                alt="Post" 
                className="post-image"
              />
              {post.images.length > 1 && (
                <>
                  <div className="image-navigation">
                    <button onClick={() => changeImage('prev')} className="image-nav-btn">
                      ←
                    </button>
                    <button onClick={() => changeImage('next')} className="image-nav-btn">
                      →
                    </button>
                  </div>
                  <div className="image-counter">
                    {currentImageIndex + 1} / {post.images.length}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Post Actions */}
          <div className="post-actions">
            <button 
              onClick={handleLike} 
              className={`action-btn ${isLiked ? 'liked' : ''}`}
              disabled={!isAuthenticated}
            >
              ❤️ {post.likeCount || 0}
            </button>
            <span className="action-info">
              💬 {post.commentCount || 0} comments
            </span>
          </div>

          {/* Comments Section */}
          <div className="comments-section">
            <h3>Comments</h3>
            
            {isAuthenticated && (
              <form onSubmit={handleCommentSubmit} className="comment-form">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  rows="3"
                  maxLength="500"
                  disabled={submittingComment}
                />
                <button 
                  type="submit" 
                  disabled={!commentText.trim() || submittingComment}
                  className="submit-comment-btn"
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            )}

            {!isAuthenticated && (
              <div className="login-prompt">
                <Link to="/login">Login</Link> to comment on this post
              </div>
            )}

            <div className="comments-list">
              {post.comments && post.comments.length > 0 ? (
                post.comments.map(comment => (
                  <div key={comment._id} className="comment">
                    <Link to={`/profile/${comment.userId?.username}`} className="comment-avatar">
                      {comment.userId?.profile?.avatar?.url ? (
                        <img src={comment.userId.profile.avatar.url} alt={comment.userId.username} />
                      ) : (
                        <div className="avatar-placeholder-small">
                          {comment.userId?.username?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </Link>
                    <div className="comment-content">
                      <div className="comment-header">
                        <Link to={`/profile/${comment.userId?.username}`} className="comment-author">
                          {comment.userId?.username}
                        </Link>
                        <span className="comment-timestamp">
                          {formatTimeAgo(comment.createdAt)}
                        </span>
                        {isAuthenticated && (comment.userId?._id === user._id || isOwner) && (
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="delete-comment-btn"
                            title="Delete comment"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <p className="comment-text">{comment.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-comments">No comments yet. Be the first to comment!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
